/**
 * Ritual Join Requests Hooks
 * Hooks for requesting to join rituals and managing join requests
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  acceptJoinRequest as acceptJoinRequestApi,
  cancelJoinRequest as cancelJoinRequestApi,
  getRitualJoinRequests,
  rejectJoinRequest as rejectJoinRequestApi,
  requestToJoinRitual as requestToJoinRitualApi,
} from "../services/ritual.service"

export function useRitualJoinRequests(
  ritualId: string,
  options?: {
    status?: "PENDING" | "ACCEPTED" | "REJECTED" | "CANCELLED"
    page?: number
    limit?: number
    enabled?: boolean
  },
) {
  const { status = "PENDING", page = 1, limit = 20, enabled = true } = options || {}

  return useQuery({
    queryKey: ["ritual-join-requests", ritualId, status, page, limit],
    queryFn: () => getRitualJoinRequests(ritualId, { status, page, limit }),
    enabled: enabled && !!ritualId,
  })
}

export function useRequestToJoinRitual(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => requestToJoinRitualApi(ritualId),
    onSuccess: () => {
      toast.success("Join request sent!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Failed to send join request")
    },
  })

  return {
    requestToJoin: mutation.mutate,
    requestToJoinAsync: mutation.mutateAsync,
    isRequesting: mutation.isPending,
  }
}

export function useAcceptJoinRequest(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (requestId: string) => acceptJoinRequestApi(ritualId, requestId),
    onSuccess: () => {
      toast.success("Join request accepted!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["ritual-join-requests", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
    onError: () => {
      toast.error("Failed to accept join request")
    },
  })

  return {
    acceptRequest: mutation.mutate,
    acceptRequestAsync: mutation.mutateAsync,
    isAccepting: mutation.isPending,
  }
}

export function useRejectJoinRequest(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      rejectJoinRequestApi(ritualId, requestId, reason),
    onSuccess: () => {
      toast.success("Join request rejected")
      queryClient.invalidateQueries({ queryKey: ["ritual-join-requests", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
    onError: () => {
      toast.error("Failed to reject join request")
    },
  })

  return {
    rejectRequest: mutation.mutate,
    rejectRequestAsync: mutation.mutateAsync,
    isRejecting: mutation.isPending,
  }
}

export function useCancelJoinRequest(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => cancelJoinRequestApi(ritualId),
    onSuccess: () => {
      toast.success("Join request cancelled")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["ritual-join-requests", ritualId] })
    },
    onError: () => {
      toast.error("Failed to cancel join request")
    },
  })

  return {
    cancelRequest: mutation.mutate,
    cancelRequestAsync: mutation.mutateAsync,
    isCancelling: mutation.isPending,
  }
}
